import React, { useEffect, useState } from "react";
import { useScrollLock } from "usehooks-ts";
import { AnimatePresence } from "motion/react";

import { useKeyPress } from "@jasperlabs/hooks";
import * as Portal from "@radix-ui/react-portal";

import { MenuToggle } from "./MenuToggle";
import { AccountToggle } from "./AccountToggle";
import MobileNavPanel from "./MobileNavPanel";
import MainNavView from "./views/MainNavView";
import AccountNavView from "./views/AccountNavView";

type ActiveView = "main" | "account";

const VIEW_ARIA_LABELS: Record<ActiveView, string> = {
	main: "Mobile navigation",
	account: "Account navigation",
};

type CrossEntityMobileNavigationProps = {
	hasOffers: boolean;
	offersCount: number;
	sellOrdersCount: number;
};

/**
 * Cross-entity mobile navigation. Exposes two header
 * buttons — an account avatar and the burger — backed by a single shared
 * slide-out panel. The panel slides in once; switching between the main and
 * account views only swaps the panel content, so the slide animation never
 * repeats. Tapping the active button, pressing Escape, or selecting a link
 * closes the panel.
 */
const CrossEntityMobileNavigation = ({
	hasOffers,
	offersCount,
	sellOrdersCount,
}: CrossEntityMobileNavigationProps) => {
	const [activeView, setActiveView] = useState<ActiveView | null>(null);

	const close = () => setActiveView(null);

	const toggleView = (view: ActiveView) =>
		setActiveView((current) => (current === view ? null : view));

	useKeyPress({
		onKeyPress: close,
		targetKey: "Escape",
	});

	const { lock, unlock } = useScrollLock({ autoLock: false });

	useEffect(() => {
		if (activeView !== null) {
			lock();
		} else {
			unlock();
		}
		// Always release the lock on unmount so the body can't stay frozen if the
		// nav unmounts while a view is open (e.g. a modal/session change).
		return () => unlock();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- sync the scroll lock with activeView only
	}, [activeView]);

	const renderView = (view: ActiveView) => {
		switch (view) {
			case "main":
				return (
					<MainNavView
						onClose={close}
						hasOffers={hasOffers}
						offersCount={offersCount}
						sellOrdersCount={sellOrdersCount}
					/>
				);
			case "account":
				return <AccountNavView onClose={close} />;
			default: {
				const exhaustiveCheck: never = view;
				return exhaustiveCheck;
			}
		}
	};

	return (
		<nav role="navigation">
			<div className="flex gap-1">
				<AccountToggle
					isOpen={activeView === "account"}
					onClick={() => toggleView("account")}
				/>
				<MenuToggle
					isOpen={activeView === "main"}
					onClick={() => toggleView("main")}
					height={18}
					width={18}
					data-testid="mobile-nav-toggle"
					aria-controls="primary-mobile-nav"
					aria-expanded={activeView === "main"}
					aria-label={
						activeView === "main" ? "Close navigation" : "Open navigation"
					}
				/>
			</div>
			<Portal.Root>
				<AnimatePresence>
					{activeView !== null && (
						<MobileNavPanel
							key="mobile-nav-panel"
							viewKey={activeView}
							ariaLabel={VIEW_ARIA_LABELS[activeView]}
						>
							{renderView(activeView)}
						</MobileNavPanel>
					)}
				</AnimatePresence>
			</Portal.Root>
		</nav>
	);
};

export default CrossEntityMobileNavigation;
